import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useEditorState } from "@tiptap/react";
import { useTranslation } from "react-i18next";
import { postProcessSelectedText } from "../../../../../../../core/api/Api";
import "./SelectionToolbar.css";

const MIN_SELECTION_CHARS = 30;
// Delay before hiding on blur — gives time for touch selection to stabilize
// and for the virtual keyboard open/close cycle to settle on mobile.
const BLUR_HIDE_DELAY_MS = 250;
// Delay before reading getBoundingClientRect after touchend —
// iOS needs a frame or two to commit selection handles to the DOM.
const TOUCH_POSITION_DELAY_MS = 120;

const SelectionToolbar = ({ editor, disabled }) => {
  const { t } = useTranslation();
  const [loadingType, setLoadingType] = useState(null);
  const [toolbarPos, setToolbarPos] = useState(null);
  // Pending AI suggestion awaiting the user's accept/reject decision.
  // Holds the original range so we can replace exactly what was selected.
  const [proposal, setProposal] = useState(null);
  const selectionRef = useRef(null);
  const blurTimerRef = useRef(null);
  const touchTimerRef = useRef(null);

  // Track Tiptap selection state (from/to/text) for all platforms
  const selState = useEditorState({
    editor,
    selector: (ctx) => {
      const { from, to } = ctx.editor.state.selection;
      if (from === to) return null;
      const text = ctx.editor.state.doc.textBetween(from, to, " ");
      if (text.trim().length < MIN_SELECTION_CHARS) return null;
      return { from, to, text: text.trim() };
    },
  });

  // Keep selectionRef in sync so handleAction always has the latest positions
  useEffect(() => {
    selectionRef.current = selState;
  }, [selState]);

  const isTouchDevice = window.matchMedia("(pointer: coarse)").matches;

  // Compute toolbar position from the native selection rect.
  // Returns false if the rect isn't ready yet (iOS timing issue).
  const computePos = useCallback(() => {
    const nativeSel = window.getSelection();
    if (!nativeSel || nativeSel.rangeCount === 0) return false;
    const rect = nativeSel.getRangeAt(0).getBoundingClientRect();
    // Zero rect = iOS hasn't painted the selection handles yet
    if (rect.width === 0 && rect.height === 0) return false;
    const left = rect.left + rect.width / 2;
    // On touch devices the native Cut/Copy/Paste bar sits ~50px above the
    // selection, so we need a larger offset to clear it. Clamp to 8px so
    // we never slide off the top of the viewport.
    const top = isTouchDevice
      ? Math.max(8, rect.top - 100)
      : rect.top - 44;
    setToolbarPos({ top, left });
    return true;
  }, [isTouchDevice]);

  // Desktop: update position whenever Tiptap selection changes
  useEffect(() => {
    if (!selState) {
      setToolbarPos(null);
      return;
    }
    computePos();
  }, [selState, computePos]);

  // Mobile: after touchend, wait for iOS to commit the selection before reading the rect
  useEffect(() => {
    const editorEl = editor?.view?.dom;
    if (!editorEl) return;

    const onTouchEnd = () => {
      clearTimeout(touchTimerRef.current);
      touchTimerRef.current = setTimeout(() => {
        // If Tiptap already updated selState this will just refresh the position.
        // If selState hasn't fired yet (iOS lag), computePos reads the native rect directly.
        if (!computePos()) {
          // One more retry if rect was still zero
          touchTimerRef.current = setTimeout(computePos, TOUCH_POSITION_DELAY_MS);
        }
      }, TOUCH_POSITION_DELAY_MS);
    };

    editorEl.addEventListener("touchend", onTouchEnd);
    return () => {
      editorEl.removeEventListener("touchend", onTouchEnd);
      clearTimeout(touchTimerRef.current);
    };
  }, [editor, computePos]);

  // Blur/focus: hide on blur, but with a delay so the virtual keyboard open/close
  // cycle on mobile doesn't immediately kill the toolbar before the selection settles.
  useEffect(() => {
    if (!editor) return;

    const onFocus = () => clearTimeout(blurTimerRef.current);

    const onBlur = () => {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = setTimeout(() => {
        // Re-check: if a valid native selection still exists, keep toolbar visible.
        // This covers the case where focus moved to the toolbar button itself.
        const nativeSel = window.getSelection();
        const stillSelected =
          nativeSel &&
          !nativeSel.isCollapsed &&
          nativeSel.toString().trim().length >= MIN_SELECTION_CHARS;
        if (!stillSelected) {
          setToolbarPos(null);
          selectionRef.current = null;
        }
      }, BLUR_HIDE_DELAY_MS);
    };

    editor.on("focus", onFocus);
    editor.on("blur", onBlur);
    return () => {
      editor.off("focus", onFocus);
      editor.off("blur", onBlur);
      clearTimeout(blurTimerRef.current);
    };
  }, [editor]);

  if (!editor) return null;

  const handleAction = async (type) => {
    if (loadingType || disabled || !selectionRef.current) return;
    const { from, to, text } = selectionRef.current;

    // Anchor the proposal card next to the selection and constrain its height
    // to the available viewport space, so the accept/reject footer is always
    // visible. Place below when there's room, otherwise flip above.
    const GAP = 8;
    const MARGIN = 16;
    let anchor = null;
    const nativeSel = window.getSelection();
    if (nativeSel && nativeSel.rangeCount > 0) {
      const rect = nativeSel.getRangeAt(0).getBoundingClientRect();
      if (rect.width || rect.height) {
        const left = rect.left + rect.width / 2;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        if (spaceBelow >= 240 || spaceBelow >= spaceAbove) {
          anchor = {
            left,
            top: rect.bottom + GAP,
            placement: "below",
            maxHeight: Math.max(120, spaceBelow - MARGIN),
          };
        } else {
          anchor = {
            left,
            top: rect.top - GAP,
            placement: "above",
            maxHeight: Math.max(120, spaceAbove - MARGIN),
          };
        }
      }
    }
    if (!anchor) {
      anchor = {
        left: toolbarPos?.left ?? window.innerWidth / 2,
        top: toolbarPos?.top ?? 80,
        placement: "below",
        maxHeight: Math.max(120, window.innerHeight - 96),
      };
    }

    setLoadingType(type);
    try {
      const response = await postProcessSelectedText({ text, type });
      const content = response?.data?.success?.content;
      if (content) {
        // Don't replace yet — present the suggestion for accept/reject.
        setProposal({ from, to, content, anchor });
      }
    } catch (_err) {
      // API errors are silent — user's original text is preserved
    } finally {
      setLoadingType(null);
    }
  };

  const acceptProposal = () => {
    if (!proposal) return;
    const { from, to, content } = proposal;
    editor.chain().focus().insertContentAt({ from, to }, content).run();
    setProposal(null);
    setToolbarPos(null);
  };

  const rejectProposal = () => {
    setProposal(null);
    setToolbarPos(null);
  };

  // Suggestion preview takes over: show the proposed text with accept/reject.
  if (proposal) {
    return createPortal(
      <div
        className="selection-toolbar selection-proposal"
        style={{
          position: "fixed",
          top: proposal.anchor?.top ?? 0,
          left: proposal.anchor?.left ?? 0,
          transform:
            proposal.anchor?.placement === "above"
              ? "translate(-50%, -100%)"
              : "translateX(-50%)",
          maxHeight: proposal.anchor?.maxHeight,
          zIndex: 9999,
        }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className="selection-proposal__label">{t("selection_suggestion")}</div>
        <div className="selection-proposal__text">{proposal.content}</div>
        <div className="selection-proposal__actions">
          <button
            type="button"
            className="selection-proposal__btn selection-proposal__btn--reject"
            onClick={rejectProposal}
          >
            {t("selection_reject")}
          </button>
          <button
            type="button"
            className="selection-proposal__btn selection-proposal__btn--accept"
            onClick={acceptProposal}
          >
            {t("selection_accept")}
          </button>
        </div>
      </div>,
      document.body
    );
  }

  if (!toolbarPos || !selState) return null;

  return createPortal(
    <div
      className="selection-toolbar"
      style={{
        position: "fixed",
        top: toolbarPos.top,
        left: toolbarPos.left,
        transform: "translateX(-50%)",
        zIndex: 9999,
      }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <button
        type="button"
        className={`selection-toolbar__btn${loadingType === "rewrite" ? " selection-toolbar__btn--loading" : ""}`}
        onClick={() => handleAction("rewrite")}
        disabled={!!loadingType || disabled}
      >
        {loadingType === "rewrite" && <span className="selection-toolbar__spinner" />}
        {t("selection_rewrite")}
      </button>

      <div className="selection-toolbar__divider" />

      <button
        type="button"
        className={`selection-toolbar__btn${loadingType === "correct_grammar" ? " selection-toolbar__btn--loading" : ""}`}
        onClick={() => handleAction("correct_grammar")}
        disabled={!!loadingType || disabled}
      >
        {loadingType === "correct_grammar" && <span className="selection-toolbar__spinner" />}
        {t("selection_correct")}
      </button>

      <div className="selection-toolbar__divider" />

      <button
        type="button"
        className={`selection-toolbar__btn${loadingType === "format" ? " selection-toolbar__btn--loading" : ""}`}
        onClick={() => handleAction("format")}
        disabled={!!loadingType || disabled}
      >
        {loadingType === "format" && <span className="selection-toolbar__spinner" />}
        {t("selection_format")}
      </button>
    </div>,
    document.body
  );
};

export default SelectionToolbar;
